import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'
import { colors } from '@/lib/colors'
import styles from './charts.module.css'

export interface PieSlice {
  name: string
  value: number
  color: string
  /** Shown in the legend instead of the raw value (e.g. formatted count). */
  label?: string
}

interface IssuesPieProps {
  slices: PieSlice[]
  onSelect?: (name: string) => void
  emptyLabel?: string
}

/**
 * Status pie: one slice per issue type. With no slices it shows a single green
 * "No issues" ring so the widget still reads at a glance. The legend moves under
 * the ring when the card is too narrow to sit beside it.
 */
export function IssuesPie({ slices, onSelect, emptyLabel = 'No issues' }: IssuesPieProps) {
  const data = slices.length > 0 ? slices : [{ name: emptyLabel, value: 1, color: colors.ok, label: '' }]
  const interactive = slices.length > 0 && onSelect

  return (
    <div className={styles.pieWrap}>
      <div className={styles.pie}>
        <div className={styles.pieChart}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius="55%"
                outerRadius="95%"
                paddingAngle={slices.length > 1 ? 2 : 0}
                isAnimationActive={false}
                onClick={interactive ? (_, index) => onSelect(data[index]!.name) : undefined}
                style={{ cursor: interactive ? 'pointer' : 'default', outline: 'none' }}
              >
                {data.map((slice) => (
                  <Cell key={slice.name} fill={slice.color} stroke="none" />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className={styles.legend}>
          {data.map((slice) => (
            <li key={slice.name}>
              <button
                type="button"
                className={`${styles.legendItem} ${interactive ? styles.legendItemButton : ''}`}
                onClick={interactive ? () => onSelect(slice.name) : undefined}
                disabled={!interactive}
              >
                <span className={styles.swatch} style={{ background: slice.color }} />
                <span className={styles.legendLabel}>{slice.name}</span>
                <span className={styles.legendValue}>{slice.label ?? slice.value}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
